import { random } from "@chatbotgang/etude/string/random";
import { reorderList } from "@polifonia/utils/vanilla/reorderList";

import {
  CUSTOM_FIELD_OPTION_DESCRIPTION_MAX_LENGTH,
  CUSTOM_FIELD_OPTION_LIMIT_PER_FIELD,
  CUSTOM_FIELD_OPTION_NAME_MAX_LENGTH,
} from "@/pages/applications/pages/customFields/constants";

/**
 * One row of the option editor.
 *
 * `id` is the server's option id, and `null` marks a row the user just added --
 * the same split the PATCH payload makes, where an item with an id updates an
 * existing option and one without creates a new one.
 *
 * `rowId` is the React key and the drag id. A new row has no server id yet, and
 * a row's name can be edited to match another's, so neither is stable enough to
 * key on; `rowId` is generated once and never changes.
 *
 * Two fields the server sends are deliberately absent. `key` is the value
 * actually stored on contacts: generated by the server, immutable, and never
 * displayed nor sent back. `active` is not stored either: see
 * `SingleSelectOptionsDraft`.
 */
export interface SingleSelectOptionDraft {
  rowId: string;
  id: number | null;
  name: string;
  description: string;
}

export type OptionSection = "active" | "archived";

/**
 * The editor's whole value: two ordered lists with the archived divider between
 * them.
 *
 * Whether an option is archived is its section, not a flag on the row. A single
 * list plus an `active` flag has a state the UI can reach and the user cannot
 * make sense of: a row sitting under the "Archived" divider still marked
 * active. Every operation then has to re-sort the list to rule it out.
 * Here that state does not exist: dragging a row across the divider moves it
 * between the two lists, and archiving it means the same thing.
 *
 * The split also lines up with how the list is sent. The contract wants one
 * array whose order is the sort order; `flattenOptionsDraft` produces it,
 * active first, archived after, deriving `active` from the section.
 */
export interface SingleSelectOptionsDraft {
  active: ReadonlyArray<SingleSelectOptionDraft>;
  archived: ReadonlyArray<SingleSelectOptionDraft>;
}

/** Where a row sits: which list, and where in it. */
export interface OptionPosition {
  section: OptionSection;
  index: number;
}

export const emptyOptionsDraft: SingleSelectOptionsDraft = {
  active: [],
  archived: [],
};

export function createOptionDraft(
  values: Partial<Omit<SingleSelectOptionDraft, "rowId">> = {},
): SingleSelectOptionDraft {
  return {
    rowId: random(),
    id: null,
    name: "",
    description: "",
    ...values,
  };
}

/**
 * Builds the editor's value from what the server returned. Options arrive in a
 * single array with an `active` flag; the two sections come straight from it,
 * each keeping the server's order.
 */
export function createOptionsDraft(
  options: ReadonlyArray<{
    id: number;
    name: string;
    description: string;
    active: boolean;
  }>,
): SingleSelectOptionsDraft {
  // One pass building both sections, rather than filtering and mapping twice
  // over each.
  const active: Array<SingleSelectOptionDraft> = [];
  const archived: Array<SingleSelectOptionDraft> = [];
  for (const option of options) {
    (option.active ? active : archived).push(
      createOptionDraft({
        id: option.id,
        name: option.name,
        description: option.description,
      }),
    );
  }
  return { active, archived };
}

/**
 * The two sections as the single ordered array the API expects, with `active`
 * derived from which section each row came from.
 */
export function flattenOptionsDraft(
  draft: SingleSelectOptionsDraft,
): Array<SingleSelectOptionDraft & { active: boolean }> {
  return [
    ...draft.active.map((option) => ({ ...option, active: true })),
    ...draft.archived.map((option) => ({ ...option, active: false })),
  ];
}

/**
 * The option list as PATCH wants it: every option the field still has, in the
 * order the editor left them, with archived ones carried as `active: false`.
 *
 * Sending the whole list is the contract, not a simplification. An existing
 * option left out is read as a stale write and rejected, because options are
 * never hard-deleted -- taking one out of use means sending it inactive.
 *
 * An untouched option still has to appear, and `{ id }` alone is enough for
 * that. Sending its name and description too would be harmless, but it would
 * also make every save look like a rename in whatever the server logs.
 */
export function toPatchOptions(
  draft: SingleSelectOptionsDraft,
  original: SingleSelectOptionsDraft,
) {
  const originalById = new Map(
    flattenOptionsDraft(original).map((option) => [option.id, option]),
  );
  return flattenOptionsDraft(draft).map((option) => {
    const name = option.name.trim();
    const description = option.description.trim();
    if (option.id === null) return { name, description, active: option.active };

    const before = originalById.get(option.id);
    const unchanged =
      before !== undefined &&
      before.name.trim() === name &&
      before.description.trim() === description &&
      before.active === option.active;
    return unchanged
      ? { id: option.id }
      : { id: option.id, name, description, active: option.active };
  });
}

export function countOptions(draft: SingleSelectOptionsDraft): number {
  return draft.active.length + draft.archived.length;
}

export function findOptionPosition(
  draft: SingleSelectOptionsDraft,
  rowId: string,
): OptionPosition | null {
  const activeIndex = draft.active.findIndex(
    (option) => option.rowId === rowId,
  );
  if (activeIndex !== -1) return { section: "active", index: activeIndex };
  const archivedIndex = draft.archived.findIndex(
    (option) => option.rowId === rowId,
  );
  if (archivedIndex !== -1)
    return { section: "archived", index: archivedIndex };
  return null;
}

/**
 * Counts code points, matching how the server measures length: Python's `len()`
 * and Postgres' `length()` both count code points, while JavaScript's
 * `String.prototype.length` counts UTF-16 code units and reports 2 for a single
 * astral character. Spreading the string iterates code points, so a name of
 * emoji is counted the same on both sides and the input's counter never
 * contradicts the server.
 */
export function countOptionCharacters(value: string): number {
  return [...value].length;
}

export type OptionNameError = "empty" | "tooLong" | "multiline" | "duplicate";
export type OptionDescriptionError = "tooLong";

/**
 * The name as it will be sent: leading and trailing whitespace removed, nothing
 * else. No case folding, no Unicode normalization, no width folding -- the
 * server compares strictly, so anything the client folded here would be a
 * duplicate the client accepted and the server then rejected.
 */
export function normalizeOptionName(name: string): string {
  return name.trim();
}

/**
 * The first rule `name` violates on its own, or `null` when it is valid.
 * `duplicate` is not decided here -- it needs the rest of the list, so it comes
 * from `getOptionNameErrors`.
 */
export function getOptionNameError(
  name: string,
): Exclude<OptionNameError, "duplicate"> | null {
  const normalized = normalizeOptionName(name);
  if (normalized.length === 0) return "empty";
  // A newline would survive the trim and reach the server inside the value.
  if (/[\n\r]/.test(normalized)) return "multiline";
  if (countOptionCharacters(normalized) > CUSTOM_FIELD_OPTION_NAME_MAX_LENGTH)
    return "tooLong";
  return null;
}

export function getOptionDescriptionError(
  description: string,
): OptionDescriptionError | null {
  return countOptionCharacters(description) >
    CUSTOM_FIELD_OPTION_DESCRIPTION_MAX_LENGTH
    ? "tooLong"
    : null;
}

/**
 * Every row's name error, keyed by `rowId`. Computed for the whole draft at
 * once because `duplicate` is the one rule a row cannot judge alone, and it
 * spans both sections: an archived option still occupies its name.
 *
 * When two rows share a name both are flagged, rather than only the later one:
 * the user has to change one of them, and highlighting a single row would point
 * at an arbitrary half of the pair.
 */
export function getOptionNameErrors(
  draft: SingleSelectOptionsDraft,
): ReadonlyMap<string, OptionNameError> {
  const options = flattenOptionsDraft(draft);
  const errors = new Map<string, OptionNameError>();
  const rowIdsByName = new Map<string, Array<string>>();
  for (const option of options) {
    const normalized = normalizeOptionName(option.name);
    const rowIds = rowIdsByName.get(normalized);
    if (rowIds) rowIds.push(option.rowId);
    else rowIdsByName.set(normalized, [option.rowId]);
  }
  for (const option of options) {
    const error = getOptionNameError(option.name);
    if (error) {
      errors.set(option.rowId, error);
      continue;
    }
    const shared = rowIdsByName.get(normalizeOptionName(option.name));
    if (shared && shared.length > 1) errors.set(option.rowId, "duplicate");
  }
  return errors;
}

export function isOptionsDraftValid(
  draft: SingleSelectOptionsDraft,
  maxItems: number = CUSTOM_FIELD_OPTION_LIMIT_PER_FIELD,
): boolean {
  const total = countOptions(draft);
  return (
    total > 0 &&
    total <= maxItems &&
    getOptionNameErrors(draft).size === 0 &&
    flattenOptionsDraft(draft).every(
      (option) => getOptionDescriptionError(option.description) === null,
    )
  );
}

function otherSection(section: OptionSection): OptionSection {
  return section === "active" ? "archived" : "active";
}

function withSections(
  section: OptionSection,
  own: ReadonlyArray<SingleSelectOptionDraft>,
  other: ReadonlyArray<SingleSelectOptionDraft>,
): SingleSelectOptionsDraft {
  return section === "active"
    ? { active: own, archived: other }
    : { active: other, archived: own };
}

/**
 * The editor's rendered list: every option in order, with the divider taking a
 * slot of its own between the two sections. Omitted when nothing is archived --
 * a divider with nothing under it is a line for its own sake.
 *
 * Sharing one list with the divider is what gives a drag an unambiguous answer:
 * the row's section is which side of that entry it came to rest on, so there is
 * no boundary case left for this module to guess at.
 */
export type OptionEntry =
  | { kind: "option"; option: SingleSelectOptionDraft }
  | { kind: "divider" };

export function toOptionEntries(
  draft: SingleSelectOptionsDraft,
): Array<OptionEntry> {
  const entries: Array<OptionEntry> = draft.active.map((option) => ({
    kind: "option" as const,
    option,
  }));
  if (draft.archived.length > 0) {
    entries.push({ kind: "divider" });
    for (const option of draft.archived) {
      entries.push({ kind: "option", option });
    }
  }
  return entries;
}

export function fromOptionEntries(
  entries: ReadonlyArray<OptionEntry>,
): SingleSelectOptionsDraft {
  const dividerIndex = entries.findIndex((entry) => entry.kind === "divider");
  const toOptions = (slice: ReadonlyArray<OptionEntry>) =>
    slice.flatMap((entry) => (entry.kind === "option" ? [entry.option] : []));
  return dividerIndex === -1
    ? { active: toOptions(entries), archived: [] }
    : {
        active: toOptions(entries.slice(0, dividerIndex)),
        archived: toOptions(entries.slice(dividerIndex + 1)),
      };
}

/**
 * Moves one entry of the rendered list, which is how a drag is applied.
 *
 * Crossing the divider is not a separate case: the entry simply lands on the
 * other side of it, and `fromOptionEntries` reads the sections off the result.
 * Nothing is written until the form is saved, so a drag that archives the wrong
 * row is undone by dragging it back.
 */
export function moveOptionEntry(
  draft: SingleSelectOptionsDraft,
  fromIndex: number,
  toIndex: number,
): SingleSelectOptionsDraft {
  const entries = toOptionEntries(draft);
  const reordered = reorderList(entries, fromIndex, toIndex);
  // `reorderList` hands back the same array when the move changes nothing.
  if (reordered === entries) return draft;
  return fromOptionEntries(reordered);
}

/**
 * Archives or restores one row through its button, putting it at the end of the
 * section it joins. Appending rather than holding some remembered position
 * keeps the move visible, because the row travels to where the user will look
 * for it next. The server stores no position for archived options anyway.
 */
export function setOptionSection(
  draft: SingleSelectOptionsDraft,
  rowId: string,
  section: OptionSection,
): SingleSelectOptionsDraft {
  const position = findOptionPosition(draft, rowId);
  if (!position || position.section === section) return draft;
  const moved = draft[position.section][position.index];
  if (!moved) return draft;
  const remaining = draft[position.section].filter(
    (_, index) => index !== position.index,
  );
  return withSections(position.section, remaining, [...draft[section], moved]);
}

export function updateOption(
  draft: SingleSelectOptionsDraft,
  rowId: string,
  values: Partial<Pick<SingleSelectOptionDraft, "name" | "description">>,
): SingleSelectOptionsDraft {
  const position = findOptionPosition(draft, rowId);
  if (!position) return draft;
  const section = draft[position.section].map((option) =>
    option.rowId === rowId ? { ...option, ...values } : option,
  );
  return withSections(
    position.section,
    section,
    draft[otherSection(position.section)],
  );
}

/**
 * Drops a row from whichever section holds it.
 *
 * The UI only offers this for an option the server has never seen, one whose
 * `id` is still `null`. Archiving is how an option that exists is taken out of
 * use, because the server never hard-deletes one and its name stays occupied;
 * a row added in this session has none of that to preserve, and archiving it
 * would ask the server to keep something it was never told about. The function
 * itself imposes no such rule -- it removes whichever row is named.
 *
 * Returns the same draft when the row is not found, so a caller working from a
 * stale id does not remount every row under it.
 */
export function removeOption(
  draft: SingleSelectOptionsDraft,
  rowId: string,
): SingleSelectOptionsDraft {
  const position = findOptionPosition(draft, rowId);
  if (!position) return draft;
  const remaining = draft[position.section].filter(
    (option) => option.rowId !== rowId,
  );
  return withSections(
    position.section,
    remaining,
    draft[otherSection(position.section)],
  );
}

/** Adds one blank row at the end of the active section. */
export function addOption(
  draft: SingleSelectOptionsDraft,
): SingleSelectOptionsDraft {
  return { ...draft, active: [...draft.active, createOptionDraft()] };
}

/**
 * Splits pasted text into one candidate name per line, dropping blank lines.
 * Returns fewer than two entries for text that was not a list, which is how the
 * caller tells an ordinary paste from a multi-line one.
 */
export function parsePastedOptionNames(text: string): Array<string> {
  return text
    .split(/\r\n|\r|\n/)
    .map((line) => normalizeOptionName(line))
    .filter((line) => line.length > 0);
}

export interface FillOptionNamesResult {
  draft: SingleSelectOptionsDraft;
  /** Names that did not fit under the option limit. */
  droppedCount: number;
}

/**
 * Writes a list of names into the rows starting at `startRowId`, creating rows
 * only once the existing ones run out. This is what a paste of 22 counties into
 * an editor holding three blank rows should do: fill those three, add the other
 * nineteen, rather than leaving three empty rows above twenty-two new ones.
 *
 * The fill stays inside the section the starting row belongs to, and new rows
 * join that section. Running past the last active row adds active rows; it
 * never continues into the archived ones. An archived option's name is the text
 * already shown for a value stored on contacts, so renaming a batch of them as
 * a side effect of pasting is not something the user asked for.
 *
 * Names beyond the option limit are reported rather than written, so the caller
 * can say how many were left out instead of dropping them silently.
 */
export function fillOptionNamesFrom(
  draft: SingleSelectOptionsDraft,
  startRowId: string,
  names: ReadonlyArray<string>,
  maxItems: number = CUSTOM_FIELD_OPTION_LIMIT_PER_FIELD,
): FillOptionNamesResult {
  const position = findOptionPosition(draft, startRowId);
  if (!position || names.length === 0) return { draft, droppedCount: 0 };

  const section = [...draft[position.section]];
  let used = 0;
  for (
    let index = position.index;
    index < section.length && used < names.length;
    index += 1
  ) {
    const option = section[index];
    const name = names[used];
    if (!option || name === undefined) continue;
    section[index] = { ...option, name };
    used += 1;
  }

  const remaining = names.slice(used);
  const room = Math.max(maxItems - countOptions(draft), 0);
  const toCreate = remaining.slice(0, room);
  section.push(...toCreate.map((name) => createOptionDraft({ name })));

  return {
    draft: withSections(
      position.section,
      section,
      draft[otherSection(position.section)],
    ),
    droppedCount: remaining.length - toCreate.length,
  };
}
